import * as Tooltip from "@radix-ui/react-tooltip";
import { Form, Formik } from "formik";
import debounce from "lodash.debounce";
import { FC, useCallback } from "react";
import { number, object, string, tuple } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { searchPlacesGeo } from "@/api";
import { Button, Select, Typography } from "@/shared/ui";
import { useOnboardingStore } from "@/stores";
import InfoIcon from "@/svgs/info.svg";
import { GeoPlace } from "@/types";

import styles from "./geo.module.scss";
import { GeoTabFormScheme } from "./geo.types";

const nonEmptyObject = object({
  value: string().optional(),
  label: string().optional(),
  coordinates: tuple([number(), number()]),
}).refine((obj) => Object.keys(obj).length > 0, {
  message: "This field is required",
});

const validationScheme = object({
  locationResidence: nonEmptyObject,
  locationOrigin: nonEmptyObject,
});

const loadOptions = async (inputValue: string): Promise<GeoPlace[]> => {
  if (!inputValue) return [];

  try {
    return await searchPlacesGeo({ q: inputValue });
  } catch (error) {
    console.error("Error fetching location data:", error);
    return [];
  }
};

const GeoTab: FC = () => {
  const currentStep = useOnboardingStore((state) => state.currentStep);
  const locationOrigin = useOnboardingStore((state) => state.userInfo.locationOrigin);
  const locationResidence = useOnboardingStore((state) => state.userInfo.locationResidence);
  const { setCurrentStep, setInfo, addPassedStep } = useOnboardingStore((state) => state.actions);

  const onSubmitHandler = ({ locationResidence, locationOrigin }: GeoTabFormScheme) => {
    setInfo({
      locationOrigin: { type: "Point", label: locationOrigin.label, coordinates: locationOrigin.coordinates },
      locationResidence: { type: "Point", label: locationResidence.label, coordinates: locationResidence.coordinates },
    });
    setCurrentStep(currentStep + 1);
    addPassedStep(currentStep);
  };

  const debouncedLoadOptions = useCallback(
    debounce((inputValue, callback) => {
      loadOptions(inputValue).then(callback);
    }, 300),
    []
  );

  return (
    <main className={styles.geoTab}>
      <Typography semi variant="title-2">
        Where do you live?
      </Typography>

      <Formik<GeoTabFormScheme>
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{
          locationOrigin: {
            value: "",
            optionLabel: "",
            label: "",
            coordinates: [0, 0],
            ...locationOrigin,
          },
          locationResidence: {
            value: "",
            optionLabel: "",
            label: "",
            coordinates: [0, 0],
            ...locationResidence,
          },
        }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        onSubmit={onSubmitHandler}
      >
        {({ values, errors, isValid, setFieldValue, validateField }) => (
          <Form className={styles.form}>
            <Select<GeoPlace>
              isClearable
              isSearchable
              placeholder="Currently living"
              noOptionsMessage={() => "No options. Try to enter another query."}
              defaultValue={values.locationResidence?.label?.length ? values.locationResidence : undefined}
              getOptionLabel={({ optionLabel, label }) => optionLabel || label}
              errorMessage={errors.locationResidence && "Required field"}
              loadOptions={debouncedLoadOptions}
              components={{
                DropdownIndicator: () => (
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <button type="button" className={styles.indicatorContainer}>
                          <InfoIcon className={styles.infoIcon} />
                        </button>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content align="end" className={styles.tooltip} sideOffset={4}>
                          <Typography variant="caption-2">
                            We won’t disclose your exact address. Only your city will be visible to others.
                          </Typography>

                          <Tooltip.Arrow className={styles.Arrow} />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                ),
              }}
              onBlur={() => validateField("locationResidence")}
              onChange={(option) => setFieldValue("locationResidence", option)}
            />
            <Select<GeoPlace>
              isClearable
              isSearchable
              placeholder="Where are you from"
              noOptionsMessage={() => "No options. Try to enter another query."}
              defaultValue={values.locationOrigin.label.length ? values.locationOrigin : undefined}
              getOptionLabel={({ optionLabel, label }) => optionLabel || label}
              errorMessage={errors.locationOrigin && "Required field"}
              loadOptions={debouncedLoadOptions}
              onBlur={() => validateField("locationOrigin")}
              onChange={(option) => setFieldValue("locationOrigin", option)}
            />

            <Button
              fullWidth
              size="big"
              type="submit"
              disabled={!isValid || Object.values(values).some((v) => !v?.label)}
            >
              Continue
            </Button>
          </Form>
        )}
      </Formik>
    </main>
  );
};

export { GeoTab };
